import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import { handleRequestError } from 'api/handleRequestError'
import axiosAuthInstance from 'api/authClient'
import {
  saveEncrypted,
  loadEncrypted,
  removeEncrypted,
} from 'utils/registrationTokenStorage/registrationTokenStorage'
import { useVehicleStore } from 'features/seller/store/vehicleStore'

const REG_TOKEN_EXPIRY = 1000 * 60 * 60 * 24

const PUBLIC_ROUTES = [
  '/login',
  '/signup',
  '/signup/success',
  '/password/forgot',
  '/password/forgot/success',
  '/password/reset',
  '/password/reset/success',
  '/link/expired',
  '/privacy-policy',
]
const REGISTRATION_ROUTES = [
  '/registration/process',
  '/account/pending',
  '/specify/role',
]

const shouldSkipProfileFetch = () => {
  const path = window.location.pathname
  return (
    PUBLIC_ROUTES.some((r) => path.startsWith(r)) ||
    REGISTRATION_ROUTES.some((r) => path.startsWith(r))
  )
}

// Auth/session store. This is the single source of truth for token/role/id/
// user — non-React modules (the axios clients, the socket) read it directly
// via `useAuthStore.getState()` / `.subscribe()`, replacing the old
// `lib/authState.js` pub/sub.
export const useAuthStore = create((set, get) => ({
  token: null,
  role: null,
  id: null,
  user: null,
  registrationToken: loadEncrypted('encrypted_registration_token') || '',
  isLoading: true,
  signature: null,
  dualAccess: null,
  dealerCountry: null,

  paymentContact: null,
  isPaymentContactCompleted: false,
  features: {
    payment_contact: false,
    payment_contact_gate: false,
    show_legacy_bank_on_bos: true,
  },
  isSwitchingRole: false,

  setToken: (value) => set({ token: value }),
  setRole: (value) => set({ role: value }),
  setIsSwitchingRole: (value) => set({ isSwitchingRole: value }),
  setId: (value) => set({ id: value }),
  setUser: (value) =>
    set((state) => ({
      user: value,
      paymentContact: value?.payment_contact ?? state.paymentContact,
      isPaymentContactCompleted:
        value?.is_payment_contact_completed ?? state.isPaymentContactCompleted,
      features: value?.features
        ? { ...state.features, ...value.features }
        : state.features,
    })),
  setSignature: (value) => set({ signature: value }),
  setDualAccess: (value) => set({ dualAccess: value }),
  setDealerCountry: (value) => set({ dealerCountry: value }),
  setPaymentContact: (value) => set({ paymentContact: value }),
  setIsPaymentContactCompleted: (value) =>
    set({ isPaymentContactCompleted: value }),
  setFeatures: (value) =>
    set((state) => ({ features: { ...state.features, ...value } })),

  setRegistrationToken: (value) => {
    set({ registrationToken: value })
    if (value) {
      saveEncrypted('encrypted_registration_token', value, REG_TOKEN_EXPIRY)
    } else {
      removeEncrypted('encrypted_registration_token')
    }
  },
  removeRegistrationToken: () => get().setRegistrationToken(''),

  logout: async () => {
    useVehicleStore.getState().RemoveEditVehicleDetails()
    set({
      token: null,
      role: null,
      id: null,
      user: null,
      signature: null,
      dualAccess: null,
      dealerCountry: null,
      paymentContact: null,
      isPaymentContactCompleted: true,
    })
  },

  // Hydrate the session from the API on app start (skipped on public /
  // registration routes). Call once on mount.
  fetchProfile: async (force = false) => {
    if (!force && shouldSkipProfileFetch()) {
      set({ isLoading: false })
      return
    }
    try {
      let profileData
      try {
        const res = await axiosAuthInstance.get('/my-profile')
        profileData = res.data
      } catch (error) {
        if (error.response?.status === 401) {
          const refreshRes = await axiosAuthInstance.post('/refresh', {})
          const newToken = refreshRes.data?.token
          if (newToken) {
            const retryRes = await axiosAuthInstance.get(
              '/my-profile',
              {},
              {
                headers: { Authorization: `Bearer ${newToken}` },
              },
            )
            profileData = retryRes.data
          } else {
            throw error
          }
        } else {
          throw error
        }
      }

      const apiRole = profileData?.role || get().role
      const apiId = profileData?.id || get().id
      const apiToken = profileData?.token || get().token

      if (apiRole && !['seller', 'buyer'].includes(apiRole)) {
        set({ token: null, role: null, user: null })
        return
      }

      const next = {
        user: profileData,
        signature: profileData?.signature || null,
        paymentContact: profileData?.payment_contact || null,
        isPaymentContactCompleted:
          profileData?.is_payment_contact_completed ??
          Boolean(profileData?.payment_contact?.first_name),
        features: profileData?.features
          ? { ...get().features, ...profileData.features }
          : get().features,
      }
      if (apiRole) next.role = apiRole
      if (apiId) next.id = apiId
      if (apiToken) next.token = apiToken
      set(next)
    } catch (error) {
      if (error.response?.status === 401) {
        set({ token: null, role: null, id: null, user: null })
      } else {
        handleRequestError(error)
      }
    } finally {
      set({ isLoading: false })
    }
  },
}))

// Drop-in replacement for the retired AuthContext's `useAuth()` — same value
// shape (state + actions + derived `isAuthenticated`).
export const useAuth = () =>
  useAuthStore(
    useShallow((state) => ({
      token: state.token,
      role: state.role,
      id: state.id,
      registrationToken: state.registrationToken,
      user: state.user,
      isLoading: state.isLoading,
      isAuthenticated: !!state.token,
      signature: state.signature,
      dualAccess: state.dualAccess,
      dealerCountry: state.dealerCountry,
      paymentContact: state.paymentContact,
      isPaymentContactCompleted: state.isPaymentContactCompleted,
      features: state.features,
      setToken: state.setToken,
      setRole: state.setRole,
      setId: state.setId,
      setUser: state.setUser,
      setSignature: state.setSignature,
      setDualAccess: state.setDualAccess,
      setDealerCountry: state.setDealerCountry,
      setRegistrationToken: state.setRegistrationToken,
      removeRegistrationToken: state.removeRegistrationToken,
      setPaymentContact: state.setPaymentContact,
      setIsPaymentContactCompleted: state.setIsPaymentContactCompleted,
      setFeatures: state.setFeatures,
      logout: state.logout,
    })),
  )
